import { useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { EmptyField } from 'components/elements/EmptyField';
import { ExternalLink } from 'components/elements/ExternalLink';
import { Label } from 'components/elements/Label';
import { Text } from 'components/elements/Text';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { ModalHeader } from 'components/layout/ModalHeader';
import { ModalStatus } from 'components/layout/ModalStatus';
import MasterDocumentModal from 'components/modals/documentModals/MasterDocumentModal';
import DocumentRelatedCompanies from 'components/widgets/DocumentModals/DocumentRelatedCompanies';
import { InfoModal } from 'components/widgets/InfoModal';
import { useBuildingPlanReview } from 'hooks/useBuildingPlanReview';
import { useBuildingPlanReviewV4 } from 'hooks/useDocuments';
import { useGetModalActions, useGetQueryParam } from 'hooks/useModal';
import { PDFMaterial } from 'types/Documents';
import { trackProjectMaterials } from 'utils/analytics';
import { parseRelatedContactsIntoList } from 'utils/contacts';
import { humanReadableArea } from 'utils/format';

const pdfMaterialContainer = {
  maxWidth: '100%',
  gap: 2,
};

const wordBreak = { wordBreak: 'break-all' };

const ellipsisContainer = {
  maxWidth: '400px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
};

export const BuildingPlanReview = ({
  enableFetchByDocumentId = false,
}: {
  enableFetchByDocumentId?: boolean;
}) => {
  let resolvedDocumentId: string | undefined;
  let result:
    | ReturnType<typeof useBuildingPlanReviewV4>
    | ReturnType<typeof useBuildingPlanReview>;

  if (enableFetchByDocumentId) {
    const documentId = useGetQueryParam('documentId');
    resolvedDocumentId = documentId || undefined;
    result = useBuildingPlanReviewV4(documentId || undefined);
  } else {
    const documentUuid = useGetQueryParam('buildingPlanReview');
    resolvedDocumentId = documentUuid || undefined;
    result = useBuildingPlanReview(documentUuid);
  }

  const { Skeleton, ...modalProps } = useGetModalActions(result.isLoading);

  const relatedContacts = useMemo(
    () => parseRelatedContactsIntoList(result.data?.metadata?.relatedcontacts || []),
    [result.data?.metadata],
  );

  const permitUrl = result.data?.document_url;
  const showWebApplication = Boolean(permitUrl);

  const hasMaterials =
    result.data?.metadata?.pdf_material_urls &&
    result.data?.metadata?.pdf_material_urls.length > 0;
  const hasRelevantMaterials = showWebApplication || hasMaterials;

  return (
    <InfoModal
      {...modalProps}
      header={
        <ModalHeader Skeleton={Skeleton} type="Building Plan Review">
          {result.data?.metadata?.permitnum}
        </ModalHeader>
      }
    >
      <Box>
        <MasterDocumentModal
          address={result.data?.metadata?.parcel_address || ''}
          city={result.data?.city ?? ''}
          state_code={result.data?.state_code ?? ''}
          isLoading={result.isLoading}
        />
        <DocumentRelatedCompanies
          contacts={relatedContacts}
          companies={result.data?.companies}
          documentId={resolvedDocumentId}
          prefetchedAiContactSuggestionsByRelatedContactId={
            result.data?.ai_related_contact_suggestions_by_contact_id
          }
          Skeleton={Skeleton}
        />

        <InfoModal.Row>
          <Label>Project Name</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.projectname ? (
                <Text fontWeight="bold">{result.data.metadata.projectname}</Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Description</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata.description ? (
                <Text>{result.data?.metadata.description}</Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Work Type</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.work_type || <EmptyField />}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Project Sqft</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.total_sq_ft ||
              result.data?.metadata?.total_sq_ft === 0 ? (
                humanReadableArea(result.data?.metadata?.total_sq_ft)
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Number of Buildings</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.num_of_buildings || <EmptyField />}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <ModalStatus activities={result.data?.activities} Skeleton={Skeleton} />

        {result.isLoading && (
          <InfoModal.Row>
            <Label>Relevant Materials</Label>
            <InfoModal.RowDetails>
              <Skeleton variant="text" noOfLines={1} />
            </InfoModal.RowDetails>
          </InfoModal.Row>
        )}
        {!result.isLoading && hasRelevantMaterials && (
          <InfoModal.Row>
            <Label>Relevant Materials</Label>
            <InfoModal.RowDetails>
              <FlexColumn sx={pdfMaterialContainer}>
                {showWebApplication && permitUrl && (
                  <ExternalLink
                    to={permitUrl}
                    onClick={() => trackProjectMaterials(permitUrl)}
                  >
                    View application
                  </ExternalLink>
                )}
                {result.data?.metadata.pdf_material_urls?.map((material: PDFMaterial) => (
                  <ExternalLink
                    key={`pdf-material-${material.document_url}`}
                    to={material.document_url}
                    onClick={() => trackProjectMaterials(material.document_url)}
                    sx={wordBreak}
                  >
                    <Box sx={ellipsisContainer}>{material.document_url}</Box>
                  </ExternalLink>
                ))}
              </FlexColumn>
            </InfoModal.RowDetails>
          </InfoModal.Row>
        )}
        {!result.isLoading && !hasRelevantMaterials && (
          <InfoModal.Row>
            <Label>Relevant Materials</Label>
            <InfoModal.RowDetails>
              <EmptyField />
            </InfoModal.RowDetails>
          </InfoModal.Row>
        )}
      </Box>
    </InfoModal>
  );
};
